import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import Animated from 'react-native-reanimated';
import Text from 'design-system/Text';
import CardRow from 'design-system/CardRow';
import InfoCard from 'design-system/InfoCard';
import SectionHeader from 'design-system/SectionHeader';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { Button, Spacer, AppView, CardView, SCROLL_INDICATOR_INSETS, withAnimatedNavigationHeader, } from 'design-system';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import CardRowContainer from 'design-system/CardRowContainer';
import useColors from 'hooks/useColors';
import { useAppSelector } from 'store/hooks';
import { selectUser } from 'reducers/selectors';
import { formatAddress } from 'utils/formatting';
import { useUserAddress } from 'hooks/useUserAddress';
import { useMarginBottom } from 'hooks/useMarginBottom';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { AccountTypeDisplay, AccountTypeDisplayLong, transferProductString } from 'features/invest/constants';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { selectTradingAccountFromId } from '../reducers/selectors';
import { isWeb } from '../../../constants';
const strings = {
    title: (t, provider, type) => t === 'JISA'
        ? `Your details must match with ${provider} ${AccountTypeDisplay[t]}`
        : `Your details must match with ${provider} ${transferProductString[type]} ${t}`,
    subTitle: (t, provider, type) => t === 'JISA'
        ? `Your details must match exactly. If anything is different, update your details in your${t === 'JISA' ? ' child’s' : ''} ${provider} ${AccountTypeDisplayLong[t]} account before continuing`
        : `Your details must match exactly. If anything is different, update your details in your ${provider} ${transferProductString[type]} ${t} account before continuing`,
    detailsWeHave: 'Details we have for you',
    nameLabel: (t) => (t === 'JISA' ? 'Child’s name' : 'Your name'),
    addressLabel: (t) => (t === 'JISA' ? 'Child’s address' : 'Your address'),
    warning: (t, provider, type) => t === 'JISA'
        ? `If these details don’t match your child’s ${provider} ${AccountTypeDisplayLong[t]}, your transfer will be rejected.`
        : `If these details don’t match your ${provider} ${transferProductString[type]} ${t}, your transfer will be rejected.`,
    continue: 'Continue',
};
const ProductTransferConfirmDetailsScreen = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const colors = useColors();
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const { provider, accountId } = route.params;
    const accountType = accountId ? 'JISA' : 'ISA';
    const providerName = provider.shortName || provider.officialName;
    const tradingAccount = useAppSelector((store) => selectTradingAccountFromId(store, accountId));
    const user = useAppSelector(selectUser);
    const { primaryAddress } = useUserAddress();
    const { name, address } = useMemo(() => {
        if (tradingAccount?.accountType === 'JISA') {
            return {
                name: `${tradingAccount.child.firstName} ${tradingAccount.child.surname}`,
                address: formatAddress(tradingAccount.child.address),
            };
        }
        return {
            name: [user.firstName, user.middleName, user.lastName].filter(Boolean).join(' '),
            address: [primaryAddress ? formatAddress(primaryAddress) : null, primaryAddress?.postcode]
                .filter(Boolean)
                .join('\n'),
        };
    }, [tradingAccount, user.firstName, user.middleName, user.lastName, primaryAddress]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const handleContinue = useCallback(() => {
        navigation.navigate('ProductTransferConfirmation', route.params);
    }, [route.params]);
    return (<AppView withFrame={false}>
          <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
            <ScreenInfoHeader title={strings.title(accountType, providerName, route.params.type)} subTitle={strings.subTitle(accountType, providerName, route.params.type)}/>
            <Spacer h={8}/>
            <SectionHeader horizontalMargins>{strings.detailsWeHave}</SectionHeader>
            <CardView>
              <CardRowContainer>
                <CardRow small tableSpacing title={strings.nameLabel(accountType)} info={name}/>
                <CardRow small tableSpacing info={address} numberOfLines={5} infoStyle={styles.addressInfo} title={strings.addressLabel(accountType)}/>
              </CardRowContainer>
            </CardView>
            <InfoCard>
              <Text TextThin-14 Secondary>
                {strings.warning(accountType, providerName, route.params.type)}
              </Text>
            </InfoCard>
          </Animated.ScrollView>
          <Button brand floating title={strings.continue} onPress={handleContinue}/>
        </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
export default ProductTransferConfirmDetailsScreen;
const styles = StyleSheet.create({
    addressInfo: {
        textAlign: 'right',
    },
});
